import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface User {
    id: string;
    email: string;
    name: string;
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<boolean>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_STORAGE_KEY = 'adnoc_auth_token';
const USER_STORAGE_KEY = 'adnoc_user';

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Check for existing auth on mount
    useEffect(() => {
        const checkAuth = () => {
            try {
                const token = localStorage.getItem(AUTH_STORAGE_KEY);
                const userData = localStorage.getItem(USER_STORAGE_KEY);
                
                if (token && userData) {
                    const parsedUser = JSON.parse(userData);
                    setUser(parsedUser);
                }
            } catch (error) {
                console.error('Error loading auth from localStorage:', error);
                // Clear corrupted data
                localStorage.removeItem(AUTH_STORAGE_KEY);
                localStorage.removeItem(USER_STORAGE_KEY);
            } finally {
                setIsLoading(false);
            }
        };

        checkAuth();
    }, []);

    const login = useCallback(async (email: string, password: string): Promise<boolean> => {
        try {
            // Simulate API call - in production, this would be a real API call
            await new Promise(resolve => setTimeout(resolve, 800)); // Simulate network delay
            
            // For demo purposes, accept any email/password combination
            // In production, validate against your backend
            if (!email || !password) {
                return false;
            }

            // Create user object
            const userData: User = {
                id: `user-${Date.now()}`,
                email: email.toLowerCase().trim(),
                name: email.split('@')[0] || 'User', // Extract name from email
            };

            // Store auth token and user data
            const token = `token-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            localStorage.setItem(AUTH_STORAGE_KEY, token);
            localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));

            setUser(userData);
            return true;
        } catch (error) {
            console.error('Login error:', error);
            return false;
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        localStorage.removeItem(USER_STORAGE_KEY);
        setUser(null);
    }, []);

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated: !!user,
                isLoading,
                login,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

